import { useCallback, useState } from 'react'
import { useDropzone, FileRejection } from 'react-dropzone'
import { ArrowExitUpIcon } from '@/common/icons/general.tsx'
import { cn } from '@/common/utilities.ts'

const MAX_AFFILIATION_IMPORT_FILE_SIZE_MB = 2
const MAX_AFFILIATION_IMPORT_FILE_SIZE =
  MAX_AFFILIATION_IMPORT_FILE_SIZE_MB * 1024 * 1024

type Props = {
  onFileChange: (file: File | null) => void
  disabled?: boolean
}

function getFileRejectionMessage(rejection: FileRejection): string {
  const firstError = rejection.errors[0]

  switch (firstError?.code) {
    case 'file-too-large':
      return `File size exceeds the ${MAX_AFFILIATION_IMPORT_FILE_SIZE_MB}MB limit.`
    case 'file-invalid-type':
      return 'Only CSV files are allowed.'
    case 'too-many-files':
      return 'Only one file can be uploaded at a time.'
    default:
      return firstError?.message ?? 'The selected file could not be uploaded.'
  }
}

export default function AffiliationProvidersFileUpload({
  onFileChange,
  disabled = false,
}: Props) {
  const [fileName, setFileName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const rejectedFile = fileRejections[0]
      if (rejectedFile) {
        setError(getFileRejectionMessage(rejectedFile))
        setFileName(null)
        onFileChange(null)
        return
      }

      const file = acceptedFiles[0]
      if (!file) {
        return
      }

      setFileName(file.name)
      setError(null)
      onFileChange(file)
    },
    [onFileChange]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled,
    accept: {
      'text/csv': ['.csv'],
    },
    maxSize: MAX_AFFILIATION_IMPORT_FILE_SIZE,
  })

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={cn(
          'rounded-md border-2 border-dashed bg-white p-10 text-center transition-colors duration-200',
          isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300',
          disabled && 'cursor-not-allowed opacity-60'
        )}
      >
        <input {...getInputProps()} />

        <div className="mb-4 flex justify-center">
          <ArrowExitUpIcon className="text-5xl text-gray-400" />
        </div>

        <p className="mb-1 font-medium text-gray-700">
          Drop your CSV file here
        </p>
        <p className="cursor-pointer text-sm text-primary underline">
          or browse files
        </p>
      </div>

      {fileName && (
        <p className="mt-3 text-center text-sm font-medium text-green-700">
          Selected: {fileName}
        </p>
      )}

      {error && (
        <p className="mt-3 text-center text-sm text-red-600">{error}</p>
      )}
    </div>
  )
}
